import React, { useEffect, useMemo, useState } from 'react';
import { DashboardMetrics } from './components/DashboardMetrics';
import { StageFilter } from './components/StageFilter';
import { PipelineStageFilter } from './components/PipelineStageFilter';
import { LeadTable } from './components/LeadTable';
import { mockLeads, STAGES } from './data/mockData';
import { LoanStage, PipelineStage } from './types';
import { Search, LayoutList, BarChart3, Sun, Moon } from 'lucide-react';
const PIPELINE_STAGES: PipelineStage[] = ['Started', 'Pre-Approved', 'Qualification'];
export function App() {
  const [activeTab, setActiveTab] = useState<'pipeline' | 'metrics'>('pipeline');
  const [activeStage, setActiveStage] = useState<LoanStage | 'All'>('All');
  const [selectedPipelineStages, setSelectedPipelineStages] = useState<PipelineStage[]>([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [isDarkMode, setIsDarkMode] = useState(false);
  useEffect(() => {
    if (isDarkMode) {
      document.documentElement.classList.add('dark');
    } else {
      document.documentElement.classList.remove('dark');
    }
  }, [isDarkMode]);
  // Calculate counts for the loan status filter bar
  const stageCounts = useMemo(() => {
    const counts: Record<string, number> = {
      All: mockLeads.length
    };
    STAGES.forEach(stage => {
      counts[stage] = mockLeads.filter(lead => lead.stage === stage).length;
    });
    return counts as Record<LoanStage | 'All', number>;
  }, []);
  // Filter leads based on active stage, pipeline stages, and search query
  const filteredLeads = useMemo(() => {
    return mockLeads.filter(lead => {
      const matchesStage = activeStage === 'All' || lead.stage === activeStage;
      const matchesPipelineStage = selectedPipelineStages.length === 0 || selectedPipelineStages.includes(lead.pipelineStage);
      const matchesSearch = lead.firstName.toLowerCase().includes(searchQuery.toLowerCase()) || lead.lastName.toLowerCase().includes(searchQuery.toLowerCase()) || lead.id.toLowerCase().includes(searchQuery.toLowerCase());
      return matchesStage && matchesPipelineStage && matchesSearch;
    });
  }, [activeStage, selectedPipelineStages, searchQuery]);
  return <div className="min-h-screen bg-gray-50 dark:bg-gray-900 text-gray-900 dark:text-gray-100 flex flex-col transition-colors duration-200" data-id="element-0">
      {/* Main Content */}
      <main className="flex-1 w-full px-4 sm:px-6 lg:px-8 py-6 flex flex-col" data-id="element-1">
        {/* Header Area */}
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-6" data-id="element-2">
          <div data-id="element-3">
            <h1 className="text-2xl font-bold text-gray-900 dark:text-white tracking-tight" data-id="element-4">
              {activeTab === 'pipeline' ? 'Pipeline Overview' : 'Performance Metrics'}
            </h1>
            <p className="text-sm text-gray-500 dark:text-gray-400 mt-1" data-id="element-5">
              {activeTab === 'pipeline' ? 'Manage and track all active files across stages.' : 'Track your production numbers and trends over time.'}
            </p>
          </div>
          <div className="flex items-center space-x-3" data-id="element-6">
            {activeTab === 'pipeline' && <div className="relative" data-id="element-7">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none" data-id="element-8">
                  <Search className="h-4 w-4 text-gray-400 dark:text-gray-500" data-id="element-9" />
                </div>
                <input type="text" placeholder="Search leads..." value={searchQuery} onChange={e => setSearchQuery(e.target.value)} className="block w-full sm:w-64 pl-9 pr-3 py-2 border border-gray-300 dark:border-gray-700 rounded-lg leading-5 bg-white dark:bg-gray-800 placeholder-gray-400 dark:placeholder-gray-500 text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 sm:text-sm transition-all" data-id="element-10" />
              </div>}
            <button onClick={() => setIsDarkMode(!isDarkMode)} className="p-2 rounded-lg bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 text-gray-600 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors" aria-label="Toggle dark mode" data-id="element-11">
              {isDarkMode ? <Sun className="w-5 h-5" data-id="element-12" /> : <Moon className="w-5 h-5" data-id="element-13" />}
            </button>
          </div>
        </div>

        {/* Tab Navigation & Stage Filter */}
        <div className="flex items-center gap-4 mb-6" data-id="element-14">
          <div className="flex space-x-1 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg p-1 w-fit shadow-sm" data-id="element-15">
            <button onClick={() => setActiveTab('pipeline')} className={`flex items-center px-4 py-2 rounded-md text-sm font-medium transition-colors ${activeTab === 'pipeline' ? 'bg-blue-50 dark:bg-blue-900/30 text-blue-700 dark:text-blue-400 shadow-sm ring-1 ring-blue-600/10 dark:ring-blue-500/20' : 'text-gray-600 dark:text-gray-400 hover:bg-gray-50 dark:hover:bg-gray-700 hover:text-gray-900 dark:hover:text-gray-200'}`} data-id="element-16">
              <LayoutList className={`w-4 h-4 mr-2 ${activeTab === 'pipeline' ? 'text-blue-600 dark:text-blue-400' : 'text-gray-400 dark:text-gray-500'}`} data-id="element-17" />
              Pipeline
            </button>
            <button onClick={() => setActiveTab('metrics')} className={`flex items-center px-4 py-2 rounded-md text-sm font-medium transition-colors ${activeTab === 'metrics' ? 'bg-blue-50 dark:bg-blue-900/30 text-blue-700 dark:text-blue-400 shadow-sm ring-1 ring-blue-600/10 dark:ring-blue-500/20' : 'text-gray-600 dark:text-gray-400 hover:bg-gray-50 dark:hover:bg-gray-700 hover:text-gray-900 dark:hover:text-gray-200'}`} data-id="element-18">
              <BarChart3 className={`w-4 h-4 mr-2 ${activeTab === 'metrics' ? 'text-blue-600 dark:text-blue-400' : 'text-gray-400 dark:text-gray-500'}`} data-id="element-19" />
              Metrics
            </button>
          </div>
          {activeTab === 'pipeline' && <PipelineStageFilter stages={PIPELINE_STAGES} selectedStages={selectedPipelineStages} onSelectionChange={setSelectedPipelineStages} data-id="element-20" />}
        </div>

        {activeTab === 'pipeline' ? <div className="flex-1 flex flex-col min-h-0 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-xl shadow-sm overflow-hidden" data-id="element-21">
            {/* Stage Filter Bar */}
            <div className="border-b border-gray-200 dark:border-gray-700 bg-gray-50/50 dark:bg-gray-800/50 px-4 pt-4" data-id="element-22">
              <StageFilter activeStage={activeStage} onStageChange={setActiveStage} stageCounts={stageCounts} data-id="element-23" />
            </div>

            {/* Master Lead Table */}
            <div className="flex-1 flex flex-col min-h-0" data-id="element-24">
              <LeadTable leads={filteredLeads} data-id="element-25" />
            </div>
          </div> : <DashboardMetrics data-id="element-26" />}
      </main>
    </div>;
}